// Gestionnaire de tiers réutilisable (Clients ou Fournisseurs).
// Liste + recherche + création + modification + désactivation (pas de suppression).
import React, { useCallback, useEffect, useState } from "react";
import api, { formatErreur } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Search, Plus, Pencil, Loader2, Users } from "lucide-react";

const RACINES = { client: "411", fournisseur: "401" };

export function TiersManager({ type, titre, libelleSingulier }) {
  const racine = RACINES[type];
  const [tiers, setTiers] = useState([]);
  const [recherche, setRecherche] = useState("");
  const [chargementListe, setChargementListe] = useState(true);

  const [dialogOuvert, setDialogOuvert] = useState(false);
  const [edition, setEdition] = useState(null); // tiers en cours de modification
  const [form, setForm] = useState({ nom: "", telephone: "", email: "", adresse: "", actif: true });
  const [erreur, setErreur] = useState("");
  const [enregistrement, setEnregistrement] = useState(false);

  const charger = useCallback(async () => {
    setChargementListe(true);
    try {
      const { data } = await api.get("/tiers", { params: { type, search: recherche } });
      setTiers(data || []);
    } catch {
      setTiers([]);
    } finally {
      setChargementListe(false);
    }
  }, [type, recherche]);

  useEffect(() => {
    const t = setTimeout(charger, 200);
    return () => clearTimeout(t);
  }, [charger]);

  const ouvrirCreation = () => {
    setEdition(null);
    setForm({ nom: "", telephone: "", email: "", adresse: "", actif: true });
    setErreur("");
    setDialogOuvert(true);
  };

  const ouvrirEdition = (t) => {
    setEdition(t);
    setForm({
      nom: t.nom, telephone: t.telephone || "", email: t.email || "",
      adresse: t.adresse || "", actif: t.actif,
    });
    setErreur("");
    setDialogOuvert(true);
  };

  const comptePrevisualise = `${racine}${(form.nom || "").trim().toUpperCase()}`;

  const enregistrer = async () => {
    setErreur("");
    if (!form.nom.trim()) {
      setErreur("Le nom est obligatoire.");
      return;
    }
    setEnregistrement(true);
    try {
      if (edition) {
        await api.put(`/tiers/${edition.id}`, {
          nom: form.nom, telephone: form.telephone, email: form.email,
          adresse: form.adresse, actif: form.actif,
        });
      } else {
        await api.post("/tiers", {
          type, nom: form.nom, telephone: form.telephone,
          email: form.email, adresse: form.adresse,
        });
      }
      setDialogOuvert(false);
      await charger();
    } catch (e) {
      setErreur(formatErreur(e.response?.data?.detail));
    } finally {
      setEnregistrement(false);
    }
  };

  return (
    <div className="fenua-fade-up" data-testid={`page-${type}s`}>
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 data-testid="titre-section" className="text-3xl font-black tracking-tight text-[#1D3D3D]">{titre}</h1>
          <p className="mt-1 text-sm text-[#1D3D3D]/70">Gérez vos {titre.toLowerCase()} en toute simplicité.</p>
        </div>
        <Button
          data-testid="bouton-nouveau-tiers"
          onClick={ouvrirCreation}
          className="bg-[#1D3D3D] hover:bg-[#2D5A5A] text-white font-semibold"
        >
          <Plus className="h-4 w-4 mr-1" /> Nouveau {libelleSingulier}
        </Button>
      </div>

      {/* Recherche */}
      <div className="relative mt-6 max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#4A8A8A]" />
        <Input
          data-testid="recherche-tiers"
          value={recherche}
          onChange={(e) => setRecherche(e.target.value)}
          placeholder={`Rechercher un ${libelleSingulier}…`}
          className="pl-10 bg-white border-[#4A8A8A]/30 focus-visible:ring-[#4A8A8A]"
        />
      </div>

      {/* Liste */}
      <div className="mt-6 bg-white border border-[#4A8A8A]/20 rounded-lg shadow-sm overflow-hidden">
        {chargementListe ? (
          <div className="p-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#4A8A8A]" /></div>
        ) : tiers.length === 0 ? (
          <div className="p-12 flex flex-col items-center text-center">
            <div className="h-14 w-14 rounded-full bg-[#E8F4F4] flex items-center justify-center mb-3">
              <Users className="h-7 w-7 text-[#4A8A8A]" />
            </div>
            <p className="text-[#1D3D3D] font-semibold">Aucun {libelleSingulier} pour le moment</p>
            <p className="text-sm text-[#1D3D3D]/60 mt-1">Cliquez sur « Nouveau {libelleSingulier} » pour commencer.</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[#E8F4F4] text-[#1D3D3D] text-left">
                <th className="px-4 py-3 font-semibold">Nom</th>
                <th className="px-4 py-3 font-semibold">Référence</th>
                <th className="px-4 py-3 font-semibold hidden md:table-cell">Téléphone</th>
                <th className="px-4 py-3 font-semibold hidden lg:table-cell">Email</th>
                <th className="px-4 py-3 font-semibold text-right">Solde</th>
                <th className="px-4 py-3 font-semibold text-right">État</th>
                <th className="px-4 py-3 w-10"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#4A8A8A]/10">
              {tiers.map((t) => (
                <tr key={t.id} data-testid={`ligne-tiers-${t.id}`} className={`hover:bg-[#E8F4F4]/40 ${!t.actif ? "opacity-50" : ""}`}>
                  <td className="px-4 py-3 font-medium text-[#1D3D3D]">{t.nom}</td>
                  <td className="px-4 py-3 font-mono text-[#4A8A8A]">{t.compte}</td>
                  <td className="px-4 py-3 text-[#1D3D3D]/80 hidden md:table-cell">{t.telephone || "—"}</td>
                  <td className="px-4 py-3 text-[#1D3D3D]/80 hidden lg:table-cell">{t.email || "—"}</td>
                  <td className="px-4 py-3 text-right text-[#1D3D3D]/80">0 XPF</td>
                  <td className="px-4 py-3 text-right">
                    <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${t.actif ? "bg-[#E8F4F4] text-[#1D3D3D]" : "bg-gray-100 text-gray-500"}`}>
                      {t.actif ? "Actif" : "Inactif"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      data-testid={`modifier-tiers-${t.id}`}
                      onClick={() => ouvrirEdition(t)}
                      className="p-1.5 rounded-md hover:bg-[#E8F4F4] text-[#4A8A8A]"
                    >
                      <Pencil className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Dialog création / édition */}
      <Dialog open={dialogOuvert} onOpenChange={setDialogOuvert}>
        <DialogContent className="bg-white" data-testid="dialog-tiers">
          <DialogHeader>
            <DialogTitle className="text-[#1D3D3D]">
              {edition ? `Modifier le ${libelleSingulier}` : `Nouveau ${libelleSingulier}`}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {erreur && <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">{erreur}</div>}

            <div className="space-y-1.5">
              <Label className="text-[#1D3D3D] font-semibold text-sm">Nom <span className="text-red-500">*</span></Label>
              <Input
                data-testid="champ-nom-tiers"
                value={form.nom}
                onChange={(e) => setForm((f) => ({ ...f, nom: e.target.value.toUpperCase() }))}
                placeholder="Ex. WATER PF"
                className="bg-white border-[#4A8A8A]/30 focus-visible:ring-[#4A8A8A] uppercase"
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-[#1D3D3D] font-semibold text-sm">Référence (générée automatiquement)</Label>
              <Input
                data-testid="champ-compte-tiers"
                value={comptePrevisualise}
                readOnly
                className="bg-[#E8F4F4] border-[#4A8A8A]/30 font-mono text-[#1D3D3D] cursor-not-allowed"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-[#1D3D3D] font-semibold text-sm">Téléphone</Label>
                <Input data-testid="champ-telephone-tiers" value={form.telephone}
                  onChange={(e) => setForm((f) => ({ ...f, telephone: e.target.value }))}
                  className="bg-white border-[#4A8A8A]/30 focus-visible:ring-[#4A8A8A]" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[#1D3D3D] font-semibold text-sm">Email</Label>
                <Input data-testid="champ-email-tiers" value={form.email}
                  onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                  className="bg-white border-[#4A8A8A]/30 focus-visible:ring-[#4A8A8A]" />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-[#1D3D3D] font-semibold text-sm">Adresse</Label>
              <Input data-testid="champ-adresse-tiers" value={form.adresse}
                onChange={(e) => setForm((f) => ({ ...f, adresse: e.target.value }))}
                className="bg-white border-[#4A8A8A]/30 focus-visible:ring-[#4A8A8A]" />
            </div>

            {edition && (
              <div className="flex items-center justify-between rounded-md bg-[#E8F4F4] px-3 py-2.5">
                <span className="text-sm font-medium text-[#1D3D3D]">{libelleSingulier} actif</span>
                <Switch
                  data-testid="switch-actif-tiers"
                  checked={form.actif}
                  onCheckedChange={(v) => setForm((f) => ({ ...f, actif: v }))}
                />
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialogOuvert(false)}>Annuler</Button>
            <Button
              data-testid="bouton-enregistrer-tiers"
              onClick={enregistrer}
              disabled={enregistrement}
              className="bg-[#1D3D3D] hover:bg-[#2D5A5A] text-white font-semibold"
            >
              {enregistrement ? <Loader2 className="h-4 w-4 animate-spin" /> : "Enregistrer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default TiersManager;
